import useTranslation from '@/utils/hooks/translation/useTranslation';
//Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Libs
import { MEDIA_LIBRARY_TYPES } from '@/lib/media/mediaLibraryTypes';
import { ALL_TIME } from '@/lib/periods';
import { LINKEDIN, TIKTOK_BUSINESS } from '@/lib/socialProfile';
// Utils
import { formatTagsList } from '@/utils/media/library';

import Checkbox from '@/components/base/Checkbox';
import Icon from '@/components/base/Icon';
import Label from '@/components/base/Label';
import Radio from '@/components/base/Radio';
// Components
import Select from '@/components/base/Select';
import SelectFilter from '@/components/base/SelectFilter';
import ActionTag from '@/components/base/Tag/ActionTag';
import DatepickerMediaLibrary from '@/components/composite/Datepicker/DatepickerMediaLibrary';
import FiltersSide from '@/components/pages/myprofile/filters/FiltersSide';

const mediaTypesCheckboxes = {
    default: [
        { locale: 'photo', value: MEDIA_LIBRARY_TYPES.TYPE_PHOTO },
        { locale: 'video', value: MEDIA_LIBRARY_TYPES.TYPE_VIDEO }
    ],
    [LINKEDIN]: [
        { locale: 'photo', value: MEDIA_LIBRARY_TYPES.TYPE_PHOTO },
        { locale: 'video', value: MEDIA_LIBRARY_TYPES.TYPE_VIDEO },
        { locale: 'document', value: MEDIA_LIBRARY_TYPES.TYPE_DOCUMENT }
    ]
};

export const Filters = ({
    toggleFilters,
    requestParams,
    setRequestParams,
    availableTags,
    isLoading,
    defaultParams
}) => {
    const { t } = useTranslation('media');
    const { activeSocialProfile } = useActiveSocialProfile();
    const network = activeSocialProfile?.social_network_type;

    const resetFilters = () => {
        setRequestParams(defaultParams);
    };

    // MEDIA TYPE FILTER //////////////////////////////////////////////////////
    const selectedMediaTypes = requestParams?.media_types;

    const setAllMediaTypes = () => {
        setRequestParams((prevState) => ({ ...prevState, media_types: [] }));
    };

    const toggleMediaType = (type) => {
        let newSelectedMediaTypes;
        if (selectedMediaTypes.includes(type)) {
            newSelectedMediaTypes = selectedMediaTypes.filter((t) => t !== type);
        } else {
            newSelectedMediaTypes = [...selectedMediaTypes, type];
        }

        setRequestParams((prevState) => ({ ...prevState, media_types: newSelectedMediaTypes }));
    };

    // STATE FILTER ////////////////////////////////////////////////////////////
    const filterOnAllState = requestParams?.used === undefined;
    const filterOnUsed = requestParams?.used === true;
    const filterOnUnused = requestParams?.used === false;

    const checkAllState = () =>
        setRequestParams((prevState) => ({ ...prevState, used: undefined }));
    const checkUsed = () => setRequestParams((prevState) => ({ ...prevState, used: true }));
    const checkUnused = () => setRequestParams((prevState) => ({ ...prevState, used: false }));

    // TAGS FILTER ////////////////////////////////////////////////////////////
    const selectedTagIds = requestParams?.tags_ids;

    let tagsSelectOptions = availableTags;
    if (selectedTagIds?.length > 0) {
        tagsSelectOptions = availableTags.reduce((nonSelectedTags, tag) => {
            if (!selectedTagIds.includes(tag.id)) {
                nonSelectedTags.push(tag);
            }
            return nonSelectedTags;
        }, []);
    }

    const selectTag = (tag) => {
        setRequestParams((prevState) => ({
            ...prevState,
            tags_ids: [...selectedTagIds, tag.id]
        }));
    };

    const removeTag = (tagId) => {
        setRequestParams((prevState) => ({
            ...prevState,
            tags_ids: selectedTagIds.filter((id) => id !== tagId)
        }));
    };

    return (
        <FiltersSide
            isOpen={true}
            toggleFilters={toggleFilters}
            onResetFilters={resetFilters}
            className="mt-10">
            {network !== TIKTOK_BUSINESS ? (
                <div>
                    <p className="mb-4 flex items-center gap-2 text-sm">
                        <Icon icon="content" className="text-icoBlue" />
                        {t('media_type')}
                    </p>
                    <ul className="space-y-2.5 text-xs">
                        <li>
                            <Label
                                fontType={
                                    !selectedMediaTypes?.length
                                        ? 'checkboxListDisabled'
                                        : 'checkboxList'
                                }
                                className="relative flex cursor-pointer pl-7">
                                <Checkbox
                                    className="absolute -top-px left-0"
                                    value="all"
                                    aria-label={t('all')}
                                    onChange={setAllMediaTypes}
                                    checked={!selectedMediaTypes?.length}
                                    disabled={!selectedMediaTypes?.length}
                                />
                                {t('all')}
                            </Label>
                        </li>
                        {(mediaTypesCheckboxes[network] ?? mediaTypesCheckboxes.default).map(
                            (type) => (
                                <li key={type.value}>
                                    <Label
                                        fontType="checkboxList"
                                        className="relative cursor-pointer pl-7">
                                        <Checkbox
                                            className="absolute -top-px left-0"
                                            value={type?.value}
                                            onChange={() => toggleMediaType(type?.value)}
                                            aria-label={t(type.locale)}
                                            checked={selectedMediaTypes?.includes(type?.value)}
                                        />
                                        {t(type?.locale)}
                                    </Label>
                                </li>
                            )
                        )}
                    </ul>
                </div>
            ) : null}
            <div>
                <p className="mb-4 flex items-center gap-2 text-sm">
                    <Icon icon="clock" className="text-icoBlue" />
                    {t('state')}
                </p>
                <ul className="space-y-2.5 text-xs">
                    <li>
                        <Label
                            fontType={filterOnAllState ? 'checkboxListDisabled' : 'checkboxList'}
                            className="relative cursor-pointer pl-7">
                            <Radio
                                disabled={filterOnAllState}
                                id="state-all"
                                name="media-state"
                                checked={filterOnAllState}
                                className="absolute -top-px left-0"
                                onChange={checkAllState}
                                value="all"
                            />
                            <span className="align-middle">{t('all')}</span>
                        </Label>
                    </li>
                    <li>
                        <Label fontType="checkboxList" className="relative cursor-pointer pl-7">
                            <Radio
                                id="state-used"
                                name="media-state"
                                checked={filterOnUsed}
                                className="absolute -top-px left-0"
                                onChange={checkUsed}
                                value="all"
                            />
                            <span className="align-middle">{t('used')}</span>
                        </Label>
                    </li>
                    <li>
                        <Label fontType="checkboxList" className="relative cursor-pointer pl-7">
                            <Radio
                                id="state-unused"
                                name="media-state"
                                checked={filterOnUnused}
                                className="absolute -top-px left-0"
                                onChange={checkUnused}
                                value="all"
                            />
                            <span className="align-middle">{t('unused')}</span>
                        </Label>
                    </li>
                </ul>
            </div>
            <div>
                <p className="mb-4 flex items-center gap-2 text-sm">
                    <Icon icon="calendar" className="text-icoBlue" />
                    {t('added_on')}
                </p>
                <DatepickerMediaLibrary
                    periodSelected={requestParams?.added_at?.period ?? ALL_TIME}
                    setRequestParams={setRequestParams}
                />
            </div>
            <div>
                <p className="mb-4 flex items-center gap-2 text-sm">
                    <Icon icon="labels" className="text-icoBlue" />
                    {t('tags')}
                </p>
                <SelectFilter
                    noBorder
                    idInput="tags-filter"
                    options={formatTagsList(tagsSelectOptions)}
                    setSelected={selectTag}
                    className="h-min text-xs text-black dark:text-white"
                    button={<Select.Button>{t('select_tag')}</Select.Button>}
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    {selectedTagIds?.map((id) => {
                        const tag = availableTags.find((t) => t.id === id);
                        if (isLoading) {
                            return (
                                <ActionTag
                                    key={id}
                                    icon="labels"
                                    afterIcon="close"
                                    variant="disabled">
                                    <span className="h-full w-6 animate-pulse rounded-md bg-icoGray-300" />
                                </ActionTag>
                            );
                        } else {
                            return (
                                <ActionTag
                                    key={id}
                                    icon="labels"
                                    afterIcon="close"
                                    iconFn={() => removeTag(id)}>
                                    {tag.tag}
                                </ActionTag>
                            );
                        }
                    })}
                </div>
            </div>
        </FiltersSide>
    );
};

export default Filters;
